import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Landmark, Globe2, Mountain, TrendingUp, Wheat,
  Palette, UtensilsCrossed, Plane, MapPin, Info,
} from "lucide-react";
import { useI18n } from "../i18n";
import ListenButton from "./ListenButton";
import ShareBadge from "./ShareBadge";
import Discussions from "./Discussions";
import CountryNews from "./CountryNews";
import PhotoGallery from "./PhotoGallery";
import SuggestCorrection from "./SuggestCorrection";
import { WikiCard } from "./GeoCards";
import Seo from "./Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_META = [
  { id: "overview", title: "Overview", icon: Globe2 },
  { id: "geography", title: "Geography & Nature", icon: Mountain },
  { id: "economy", title: "Economy", icon: TrendingUp },
  { id: "agriculture", title: "Agriculture", icon: Wheat, eco: { label: "Explore farming on FarmersPulse.com", url: "https://farmerspulse.com" } },
  { id: "culture", title: "Culture & Society", icon: Palette },
  { id: "food", title: "Food & Cuisine", icon: UtensilsCrossed, eco: { label: "Discover dishes on FoodPulse.net", url: "https://foodpulse.net" } },
  { id: "tourism", title: "Travel & Tourism", icon: Plane, eco: { label: "Plan your trip on TravelerPulse.pro", url: "https://travelerpulse.pro" } },
];

export default function DistrictPage() {
  const { country, state, district } = useParams();
  const { tr } = useI18n();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [sections, setSections] = useState(null);
  const [towns, setTowns] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [pilotCities, setPilotCities] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setData(null);
    setSections(null);
    setError(false);
    axios.get(`${API}/district/${country}/${state}/${district}/cities`)
      .then((r) => setPilotCities(r.data.cities))
      .catch(() => setPilotCities([]));
    axios.get(`${API}/district/${country}/${state}/${district}`)
      .then((r) => {
        setData(r.data);
        if (r.data.sections) {
          setSections(r.data.sections);
          setTowns(r.data.towns || []);
        } else {
          setGenerating(true);
          axios.post(`${API}/district/${country}/${state}/${district}/enrich`, {}, { timeout: 180000 })
            .then((e) => { setSections(e.data.sections); setTowns(e.data.towns || []); })
            .catch(() => toast.error("Content generation is busy — please refresh in a moment."))
            .finally(() => setGenerating(false));
        }
      })
      .catch(() => setError(true));
  }, [country, state, district]);

  const jsonLd = useMemo(() => data && {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "AdministrativeArea", name: `${data.name} district`,
        url: `https://worldpulse.pro/country/${data.country_slug}/${data.state_slug}/${data.slug}`,
        containedInPlace: { "@type": "AdministrativeArea", name: data.state_name } },
      { "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "World", item: "https://worldpulse.pro/" },
        { "@type": "ListItem", position: 2, name: data.country_name, item: `https://worldpulse.pro/country/${data.country_slug}` },
        { "@type": "ListItem", position: 3, name: data.state_name, item: `https://worldpulse.pro/country/${data.country_slug}/${data.state_slug}` },
        { "@type": "ListItem", position: 4, name: data.name, item: `https://worldpulse.pro/country/${data.country_slug}/${data.state_slug}/${data.slug}` },
      ] },
    ],
  }, [data]);

  if (error) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center gap-4">
        <p className="font-bold text-[#0F2557]">{tr("This district is not in the pilot layer yet.")}</p>
        <Link to={`/place/${district}`} className="text-sm font-bold text-[#2563EB] hover:underline">{tr("Open the live place page instead")} →</Link>
      </div>
    );
  }
  if (!data) {
    return (
      <div data-testid="district-loading" className="min-h-[60vh] flex items-center justify-center gap-3 text-[#2563EB]">
        <Loader2 className="animate-spin" /> <span className="font-bold">{tr("Opening this district's portal…")}</span>
      </div>
    );
  }

  const hasPhoto = !!data.photo?.url;

  return (
    <div data-testid="district-page" className="pb-20">
      <Seo
        title={`${data.name} District, ${data.state_name} — WorldPulse.pro`}
        description={`${data.name} district in ${data.state_name}, ${data.country_name} (headquarters ${data.hq}) — geography, economy, culture, food and travel on WorldPulse.pro.`}
        canonical={`https://worldpulse.pro/country/${data.country_slug}/${data.state_slug}/${data.slug}`}
        image={data.photo?.url}
        jsonLd={jsonLd}
      />

      <div className={`relative overflow-hidden ${hasPhoto ? "py-16 lg:py-24" : "py-12 lg:py-16"}`}
        style={!hasPhoto ? { background: "linear-gradient(135deg, #EAF4FD 0%, #F6FAFF 45%, #E8F7F0 100%)" } : undefined}>
        {hasPhoto && (
          <>
            <img data-testid="district-photo-hero" src={data.photo.url} alt={`${data.name} landscape`} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F2557]/85 via-[#0F2557]/40 to-[#0F2557]/20" />
            <span className="absolute bottom-2 right-3 text-[0.55rem] font-bold text-white/60 z-10">📷 {data.photo.credit}</span>
          </>
        )}
        <div className="relative max-w-6xl mx-auto px-4 lg:px-8">
          <nav className={`flex flex-wrap items-center gap-1.5 text-sm font-bold ${hasPhoto ? "text-white/90" : "text-[#2563EB]"}`} aria-label="Breadcrumb">
            <Link to="/" className="inline-flex items-center gap-1.5 hover:underline"><ArrowLeft size={14} /> {tr("World")}</Link>
            <span className="opacity-60">›</span>
            <Link to={`/country/${data.country_slug}`} data-testid="district-breadcrumb-country" className="hover:underline">{data.flag} {data.country_name}</Link>
            <span className="opacity-60">›</span>
            <Link to={`/country/${data.country_slug}/${data.state_slug}`} data-testid="district-breadcrumb-state" className="hover:underline">{data.state_name}</Link>
            <span className="opacity-60">›</span>
            <span className="opacity-90">{data.name}</span>
          </nav>
          <div className="flex flex-wrap items-end justify-between gap-6 mt-5">
            <div>
              <span className={`text-[0.62rem] font-extrabold tracking-widest uppercase px-3 py-1 rounded-full ${hasPhoto ? "bg-white/15 text-white border border-white/30 backdrop-blur-sm" : "bg-white text-[#1D4ED8] border border-[#BFD4F2]"}`}>{tr("District")}</span>
              <h1 data-testid="district-title" className={`text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-3 ${hasPhoto ? "text-white drop-shadow-md" : "text-[#0F2557]"}`}>
                {data.name}
              </h1>
              <div className={`flex flex-wrap gap-x-5 gap-y-1 mt-3 text-sm font-semibold ${hasPhoto ? "text-white/85" : "text-[#475569]"}`}>
                <span className="flex items-center gap-1.5"><Landmark size={14} /> {tr("Headquarters")}: {data.hq}</span>
              </div>
            </div>
            {sections?.overview && <ListenButton text={`${data.name}. ${sections.overview.map((p) => tr(p)).join(" ")}`} label={tr("Listen")} />}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-3 gap-10 mt-10">
          <div className="lg:col-span-2">
            {generating && (
              <div data-testid="district-generating" className="border border-[#BFD4F2] bg-[#F6FAFF] rounded-3xl p-6 flex items-center gap-3 text-[#1D4ED8] mb-8">
                <Loader2 className="animate-spin shrink-0" size={18} />
                <span className="text-sm font-bold">{tr("Writing this district's portal sections — about 30 seconds…")}</span>
              </div>
            )}
            {SECTION_META.map((meta) => {
              const paras = sections?.[meta.id];
              const Icon = meta.icon;
              return (
                <section key={meta.id} id={meta.id} className="scroll-mt-32 mb-12" data-testid={`district-section-${meta.id}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-xl bg-[#EAF2FD] flex items-center justify-center"><Icon size={17} className="text-[#2563EB]" /></span>
                    <h2 className="text-xl font-extrabold text-[#0F2557] flex-1">{tr(meta.title)}</h2>
                    {paras?.length > 0 && <ListenButton compact text={`${tr(meta.title)}. ${paras.map((p) => tr(p)).join(" ")}`} />}
                  </div>
                  <div className="mt-4 space-y-4">
                    {paras?.length ? paras.map((p, i) => (
                      <p key={i} className="text-[1.05rem] text-[#1E293B] leading-[1.85]">{tr(p)}</p>
                    )) : (
                      <div className="space-y-2">
                        <div className="h-3.5 rounded-full bg-[#EAF2FD] animate-pulse w-full" />
                        <div className="h-3.5 rounded-full bg-[#EAF2FD] animate-pulse w-11/12" />
                      </div>
                    )}
                  </div>
                  {meta.eco && paras?.length > 0 && (
                    <a href={meta.eco.url} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-2 mt-4 text-xs font-extrabold text-[#0D9488] bg-[#F0FDFA] border border-[#99F6E4] rounded-full px-4 py-2 hover:bg-[#CCFBF1] transition-colors">
                      {tr(meta.eco.label)}
                    </a>
                  )}
                </section>
              );
            })}

            <section id="towns" className="scroll-mt-32 mb-12" data-testid="district-section-towns">
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-xl bg-[#FFF7ED] flex items-center justify-center"><MapPin size={17} className="text-[#EA580C]" /></span>
                <h2 className="text-xl font-extrabold text-[#0F2557]">{tr("Major Towns")}</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-3 mt-4">
                {towns.map((t) => (
                  <Link key={t.name} to={`/place/${t.name.toLowerCase().replace(/\s+/g, "-")}`} data-testid="district-town-card"
                    className="border border-[#E5EDF7] rounded-2xl p-4 hover:border-[#EA580C] hover:bg-[#FFF7ED] transition-colors group">
                    <div className="font-extrabold text-sm text-[#0F2557] group-hover:text-[#EA580C]">{t.name}</div>
                    <p className="text-xs text-[#64748B] mt-1 leading-relaxed">{tr(t.role)}</p>
                  </Link>
                ))}
                {!towns.length && <p className="text-sm text-[#64748B]">{tr("Town directory is being assembled.")}</p>}
              </div>
            </section>

            {pilotCities.length > 0 && (
              <section id="pilot-cities" className="scroll-mt-32 mb-12" data-testid="district-pilot-cities">
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-xl bg-[#F0FDF4] flex items-center justify-center"><Landmark size={17} className="text-[#16A34A]" /></span>
                  <h2 className="text-xl font-extrabold text-[#0F2557]">{tr("City Portals")}</h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mt-4">
                  {pilotCities.map((c) => (
                    <Link key={c.slug} to={`/country/${data.country_slug}/${data.state_slug}/${data.slug}/${c.slug}`} data-testid="district-city-portal-card"
                      className="border border-[#E5EDF7] rounded-xl px-3.5 py-2.5 hover:border-[#16A34A] hover:bg-[#F0FDF4] transition-colors group">
                      <div className="text-sm font-extrabold text-[#0F2557] group-hover:text-[#16A34A]">{c.name}</div>
                      <div className="text-[0.6rem] font-semibold text-[#64748B] mt-0.5">{tr("Open city portal")} →</div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            <CountryNews path={`/district/${data.country_slug}/${data.state_slug}/${data.slug}/news`} name={data.name} />

            <PhotoGallery gkey={`district-${data.country_slug}-${data.state_slug}-${data.slug}`} query={`${data.name} ${data.state_name}`} name={data.name} />

            <Discussions dkey={`${data.country_slug}-${data.state_slug}-${data.slug}`} placeName={data.name} />

            {sections && (
              <div className="flex flex-wrap items-center gap-4 mt-2">
                <SuggestCorrection gkey={`${data.country_slug}-${data.state_slug}-${data.slug}`} placeName={data.name}
                  sections={["overview", "geography", "economy", "agriculture", "culture", "food", "tourism", "photos", "other"]} />
              </div>
            )}
          </div>

          <div className="space-y-5">
            <div data-testid="district-quick-facts" className="border border-[#BFD4F2] rounded-3xl p-5 bg-white">
              <span className="flex items-center gap-2 font-bold text-sm text-[#1D4ED8]"><Info size={15} /> {tr("Quick Facts")}</span>
              <div className="mt-3 space-y-2.5 text-sm leading-relaxed">
                <div><span className="font-extrabold text-[#0F2557]">{tr("Type")}: </span><span className="text-[#475569]">{tr("District")}</span></div>
                <div><span className="font-extrabold text-[#0F2557]">{tr("Headquarters")}: </span><span className="text-[#475569]">{data.hq}</span></div>
                <div><span className="font-extrabold text-[#0F2557]">{tr("State")}: </span><span className="text-[#475569]">{data.state_name}</span></div>
                <div><span className="font-extrabold text-[#0F2557]">{tr("Country")}: </span><span className="text-[#475569]">{data.flag} {data.country_name}</span></div>
              </div>
            </div>
            <WikiCard wiki={data.wiki} />
            <ShareBadge url={`https://worldpulse.pro/country/${data.country_slug}/${data.state_slug}/${data.slug}`} />
            {data.siblings?.length > 0 && (
              <div data-testid="district-siblings-card" className="border border-[#99F6E4] rounded-3xl p-5 bg-[#F0FDFA]">
                <span className="font-bold text-sm text-[#0D9488]">{tr("Other districts of")} {data.state_name}</span>
                <div className="flex flex-wrap gap-2 mt-3">
                  {data.siblings.map((s) => (
                    <Link key={s.slug} to={`/country/${data.country_slug}/${data.state_slug}/${s.slug}`} data-testid="district-sibling-chip"
                      className="text-xs font-bold text-[#0F2557] bg-white border border-[#CBD5E1] rounded-full px-3 py-1.5 hover:border-[#0D9488] hover:text-[#0D9488] transition-colors">
                      {s.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
